import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  LuSearch, LuTrash2, LuDownload, LuTarget, LuCalendar, LuMail, LuPhone,
  LuFilter, LuCircleCheck, LuUser, LuClock, LuHistory, LuUserPlus
} from 'react-icons/lu';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ModalPortal from '../../components/ui/ModalPortal';
import EmailReminderModal from '../../components/EmailReminderModal';
import { LuX } from 'react-icons/lu';

const LeadManagement = ({ onCountChange, onOpenAddModal }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [reminderRecipient, setReminderRecipient] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'manual' | 'website'

  // States for selection and filtering
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [dateFilterMode, setDateFilterMode] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(leads.length);
    }
  }, [leads.length, onCountChange]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await axios.get(`${apiUrl}/api/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setLeads(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      await axios.delete(`${apiUrl}/api/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leads.filter(lead => lead._id !== id));
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } catch (err) {
      console.error('Error deleting lead:', err);
      alert('Failed to delete lead');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} leads?`)) return;

    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      for (const id of selectedIds) {
        await axios.delete(`${apiUrl}/api/leads/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setLeads(leads.filter(lead => !selectedIds.includes(lead._id)));
      setSelectedIds([]);
      setSelectionMode(false);
    } catch (err) {
      console.error('Error in batch delete:', err);
      alert('Failed to complete batch delete');
    }
  };

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch =
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.whatsapp && lead.whatsapp.includes(searchTerm));

      if (!matchesSearch) return false;

      // Source Filter (All vs Manual vs Website)
      const isManual = Boolean(lead.isManual || lead.addedBy || lead.source?.toLowerCase().includes('manual'));
      if (sourceFilter === 'manual' && !isManual) return false;
      if (sourceFilter === 'website' && isManual) return false;

      if (dateFilterMode === 'today') {
        return moment(lead.createdAt).isSame(moment(), 'day');
      }
      if (dateFilterMode === 'yesterday') {
        return moment(lead.createdAt).isSame(moment().subtract(1, 'day'), 'day');
      }
      if (dateFilterMode === 'last7') {
        return moment(lead.createdAt).isAfter(moment().subtract(7, 'days').startOf('day'));
      }
      if (dateFilterMode === 'last30') {
        return moment(lead.createdAt).isAfter(moment().subtract(30, 'days').startOf('day'));
      }
      if (dateFilterMode === 'range') {
        let valid = true;
        if (startDate) {
          valid = valid && moment(lead.createdAt).isSameOrAfter(moment(startDate).startOf('day'));
        }
        return valid;
      }

      return true;
    });
  }, [leads, searchTerm, sourceFilter, dateFilterMode, startDate, endDate]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sourceFilter, dateFilterMode, startDate, endDate]);

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeads = useMemo(() => {
    return filteredLeads.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredLeads, indexOfFirstItem, indexOfLastItem]);

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredLeads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredLeads.map(l => l._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const exportToCSV = () => {
    const dataToExport = selectedIds.length > 0
      ? filteredLeads.filter(l => selectedIds.includes(l._id))
      : filteredLeads;

    const headers = ['Name', 'Email', 'WhatsApp', 'Source', 'Date', 'Time'];
    const rows = dataToExport.map(lead => [
      lead.name,
      lead.email,
      lead.whatsapp || 'N/A',
      lead.source,
      moment(lead.createdAt).format('YYYY-MM-DD'),
      moment(lead.createdAt).format('HH:mm:ss')
    ]);

    let csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `landing_leads_${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={6} />;
  }

  return (
    <div className="p-6 space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Leads', value: leads.length, icon: LuTarget, iconBg: 'bg-primary-100 dark:bg-primary-900/30', iconColor: 'text-primary-600 dark:text-primary-400' },
          { label: 'Captured Today', value: leads.filter(r => moment(r.createdAt).isSame(moment(), 'day')).length, icon: LuClock, iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
          { label: 'With WhatsApp', value: leads.filter(r => r.whatsapp).length, icon: LuPhone, iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
          { label: 'Filtered', value: filteredLeads.length, icon: LuHistory, iconBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-600 dark:text-purple-400' },
        ].map((stat, i) => (
          <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-card">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-grow w-full flex items-center gap-3">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, or whatsapp..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-xs font-bold text-gray-800 dark:text-gray-200 cursor-pointer focus:ring-2 focus:ring-indigo-500/30 outline-none transition-all whitespace-nowrap"
            >
              <option value="all">All Entry Sources</option>
              <option value="manual">🏷️ Manual Entries Only</option>
              <option value="website">🌐 Website Submissions Only</option>
            </select>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowDateFilters(!showDateFilters)}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap cursor-pointer ${showDateFilters || dateFilterMode !== 'all'
                  ? "bg-primary-50 dark:bg-primary-900/30 text-primary-600 border border-primary-200 dark:border-primary-800"
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
            >
              <LuCalendar />
              Date Filters {dateFilterMode !== 'all' ? `(${dateFilterMode.toUpperCase()})` : ''}
            </button>

            <button
              onClick={() => setSelectionMode(!selectionMode)}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${selectionMode ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"}`}
            >
              <LuCircleCheck />
              {selectionMode ? "Active" : "Select"}
            </button>

            <button
              onClick={exportToCSV}
              className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap cursor-pointer"
            >
              <LuDownload size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {showDateFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 flex flex-col md:flex-row gap-4 items-end animate-in fade-in duration-200">
            <div className="w-full md:w-auto">
              <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">Date Range Preset</label>
              <select
                value={dateFilterMode}
                onChange={(e) => setDateFilterMode(e.target.value)}
                className="w-full md:w-48 px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white cursor-pointer"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="range">Custom Range</option>
              </select>
            </div>

            {dateFilterMode === 'range' && (
              <>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {dateFilterMode !== 'all' && (
              <button
                type="button"
                onClick={() => {
                  setDateFilterMode('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="px-4 py-2 text-xs font-bold text-red-600 dark:text-red-400 underline hover:no-underline cursor-pointer"
              >
                Reset Date Filter
              </button>
            )}
          </div>
        )}
      </div>

      {selectionMode && (
        <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
              {selectedIds.length} of {filteredLeads.length} selected
            </span>
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-bold text-primary-600 dark:text-primary-400 underline hover:no-underline cursor-pointer"
            >
              {selectedIds.length === filteredLeads.length && filteredLeads.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={handleBatchDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
            >
              <LuTrash2 className="w-4 h-4" /> Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                {selectionMode && (
                  <th className="px-4 py-4 text-center w-12">
                    <input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === filteredLeads.length && filteredLeads.length > 0} className="rounded" />
                  </th>
                )}
                <th className="px-4 py-4 text-left text-sm font-semibold">Lead Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">WhatsApp</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Source</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-center text-sm font-semibold w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={selectionMode ? 7 : 6} className="px-6 py-16 text-center text-gray-500 font-bold">
                    No leads found matching your search.
                  </td>
                </tr>
              ) : (
                currentLeads.map((lead) => (
                  <tr
                    key={lead._id}
                    className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer ${selectedIds.includes(lead._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : (lead.isManual || lead.source?.toLowerCase().includes('manual')) ? 'bg-indigo-50/30 dark:bg-indigo-950/20' : ''}`}
                    onClick={() => selectionMode ? toggleSelectOne(lead._id) : setSelectedLead(lead)}
                  >
                    {selectionMode && (
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(lead._id)}
                          onChange={() => toggleSelectOne(lead._id)}
                          className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white text-sm whitespace-nowrap">{lead.name}</span>
                        {(lead.isManual || lead.addedBy || lead.source?.toLowerCase().includes('manual')) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shadow-xs" title={`Added by ${lead.addedBy || 'Admin'}`}>
                            🏷️ Manual
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-primary-600 dark:text-primary-400 whitespace-nowrap truncate max-w-[220px]" title={lead.email}>{lead.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap truncate max-w-[160px]" title={lead.whatsapp || 'N/A'}>{lead.whatsapp || 'N/A'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${
                        (lead.isManual || lead.source?.toLowerCase().includes('manual'))
                          ? 'bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800'
                          : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800'
                      }`}>
                        {lead.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{moment(lead.createdAt).format('MMM DD, YYYY')}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap w-28">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(lead._id); }}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-xl transition-colors cursor-pointer"
                          title="Delete Lead"
                        >
                          <LuTrash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setReminderRecipient({ name: lead.name, email: lead.email }); }}
                          className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 border border-primary-200 dark:border-primary-800/50 rounded-xl transition-colors cursor-pointer"
                          title="Set Email Reminder"
                        >
                          <LuMail className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              Showing <span className="font-bold text-gray-900 dark:text-white">{indexOfFirstItem + 1}</span> to <span className="font-bold text-gray-900 dark:text-white">{Math.min(indexOfLastItem, filteredLeads.length)}</span> of <span className="font-bold text-gray-900 dark:text-white">{filteredLeads.length}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${currentPage === page ? 'bg-primary-600 text-white shadow-sm shadow-primary-500/30' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                      >
                        {page}
                      </button>
                    );
                  }
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-1 text-xs text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ModalPortal open={Boolean(selectedLead)} onClose={() => setSelectedLead(null)}>
        {selectedLead && (
          <>
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Lead Details</h2>
              <button type="button" onClick={() => setSelectedLead(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl text-gray-500 dark:text-gray-300">
                <LuX />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Name</label><p className="font-bold text-gray-900 dark:text-white">{selectedLead.name}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Email</label><p className="font-bold text-primary-600">{selectedLead.email}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">WhatsApp</label><p className="font-bold text-gray-900 dark:text-white">{selectedLead.whatsapp || 'N/A'}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Source</label><p className="font-bold text-gray-900 dark:text-white">{selectedLead.source || 'N/A'}</p></div>
                <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl sm:col-span-2">
                  <label className="text-[10px] font-black uppercase text-gray-400">Submitted</label>
                  <p className="font-bold flex items-center gap-2 mt-1 text-gray-900 dark:text-white">
                    <LuCalendar className="w-4 h-4 text-gray-400" />
                    {moment(selectedLead.createdAt).format('MMM DD, YYYY hh:mm A')}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3">
              <button type="button" onClick={() => setSelectedLead(null)} className="px-6 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold text-xs uppercase cursor-pointer">Close</button>
              <button type="button" onClick={() => { setReminderRecipient({ name: selectedLead.name, email: selectedLead.email }); setSelectedLead(null); }} className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold text-xs uppercase flex items-center gap-2 cursor-pointer"><LuMail className="w-4 h-4" /> Send Reminder</button>
              <button type="button" onClick={() => { handleDelete(selectedLead._id); setSelectedLead(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Delete</button>
            </div>
          </>
        )}
      </ModalPortal>

      <EmailReminderModal
        isOpen={Boolean(reminderRecipient)}
        onClose={() => setReminderRecipient(null)}
        recipient={reminderRecipient}
      />
    </div>
  );
};

export default LeadManagement;
